import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { ensureSpSessionHydrated } from '@/features/auth/api/useAuth'
import {
  captureSpOtpChannelSelected,
  captureSpSurveyLinkClicked,
  parseSurveyLinkChannel,
  shouldCaptureSurveyLinkClick,
} from '@/lib/analytics'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  formatUsMobileDisplay,
  formatUsMobileOnBlur,
  isValidUsMobileNumber,
  normalizeUsMobileForStorage,
} from '@/features/account/lib/account-phone-utils'
import {
  persistWebSurveyOnboardingSession,
  persistWebSurveyPhone,
  persistWebSurveyPhoneVerified,
  persistWebSurveySurveyFirst,
  readWebSurveyInstrumentId,
  readWebSurveyOnboardingSession,
  readWebSurveyPhone,
  readWebSurveySurveyFirst,
} from '@/features/auth/lib/constants'
import {
  buildSurveyFirstOnboardingSession,
  resolvePendingOnboardingSessionId,
} from '../lib/pending-survey-session'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { useLockoutRemaining } from '@/features/auth/hooks/useLockoutRemaining'
import { ApiError } from '@/api/http/types'
import {
  useAcceptSpPrivacy,
  useAcceptSpTerms,
  useCheckWebSurveyPhone,
  useInstrumentLinkStart,
  useInstrumentOtpResend,
  useInstrumentOtpSend,
  useInstrumentOtpVerify,
} from '../api/instrument-onboarding-mutations'
import {
  ONBOARDING_PHONE_DATASET_ERROR,
  ONBOARDING_PHONE_ERROR,
  SP_INSTRUMENT_OTP_RESEND_COOLDOWN_SECONDS,
  SP_PRIVACY_POLICY_VERSION,
  SP_TERMS_POLICY_VERSION,
} from '../lib/constants'
import {
  ensureInstrumentLinkSessionOrLogin,
  INSTRUMENT_OTP_EXPIRED_MESSAGE,
  instrumentInfoPath,
  resolveConsentMutationError,
  resolveInstrumentConsentStep,
  resolveInstrumentLinkStartError,
  resolveOtpResendError,
  resolveOtpSendError,
  resolveOtpVerifyError,
  resolveRetryConsentStep,
  routeInstrumentLinkStartFlow,
} from '../lib/instrument-link-onboarding.helpers'
import { parseInstrumentLinkToken } from '../lib/instrument-link-url'
import {
  INVALID_INSTRUMENT_LINK_MESSAGE,
  instrumentLinkLoginState,
} from '../lib/instrument-link-routing'
import type {
  ConsentDeclineKind,
  InstrumentOnboardingStep,
  SpInstrumentLinkStartData,
  SpInstrumentOtpSendData,
  SpInstrumentOtpVerifyData,
  SpOnboardingChannel,
} from '../lib/types'

function surveyFirstPhoneDisplay(): string {
  const stored = readWebSurveyPhone()
  return stored ? formatUsMobileDisplay(stored) : ''
}

function applyResolvedSurveyFirstSession(
  userId: string | null | undefined,
  setters: {
    setSession: (session: SpInstrumentLinkStartData) => void
    setChannel: (channel: SpOnboardingChannel) => void
    setStep: (step: InstrumentOnboardingStep) => void
    setServerError: (error: string | null) => void
  },
): boolean {
  const sessionId = resolvePendingOnboardingSessionId(
    readWebSurveyOnboardingSession(),
    userId,
  )
  if (!sessionId) return false
  persistWebSurveyOnboardingSession(sessionId)
  persistWebSurveySurveyFirst(readWebSurveyInstrumentId() || undefined)
  setters.setServerError(null)
  setters.setSession(buildSurveyFirstOnboardingSession(sessionId))
  setters.setChannel('sms')
  setters.setStep('channel')
  return true
}

export function useInstrumentLinkOnboarding(options?: {
  linkTokenOverride?: string | null
  preferSms?: boolean
  /** Sign-up share `/instruments/:id` (or legacy `/s/:slug`): Privacy + Terms, then Instrument Info. */
  signupShareFlow?: boolean
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.toString()
  const surveyFirstPhone =
    searchParams.get('surveyFirst') === '1' || readWebSurveySurveyFirst()
  const queryToken = useMemo(
    () => parseInstrumentLinkToken(searchQuery),
    [searchQuery]
  )
  const override = options?.linkTokenOverride
  const linkToken = useMemo(() => {
    if (override === undefined) return queryToken
    const trimmed = override?.trim() ?? ''
    return trimmed.length >= 10 ? trimmed : null
  }, [override, queryToken])
  const preferSms = options?.preferSms === true
  const signupShareFlow = options?.signupShareFlow === true

  const { mutate: startInstrumentLink, isPending: linkStartPending } =
    useInstrumentLinkStart()
  const otpSend = useInstrumentOtpSend()
  const otpResend = useInstrumentOtpResend()
  const otpVerify = useInstrumentOtpVerify()
  const phoneCheck = useCheckWebSurveyPhone()
  const acceptPrivacy = useAcceptSpPrivacy()
  const acceptTerms = useAcceptSpTerms()
  const storeUserId = useSessionStore((s) => s.user?.id)

  const [step, setStep] = useState<InstrumentOnboardingStep>(() =>
    surveyFirstPhone ? 'channel' : 'loading',
  )
  const [session, setSession] = useState<SpInstrumentLinkStartData | null>(() => {
    if (!surveyFirstPhone) return null
    const sessionId = resolvePendingOnboardingSessionId(
      readWebSurveyOnboardingSession(),
      useSessionStore.getState().user?.id,
    )
    return sessionId ? buildSurveyFirstOnboardingSession(sessionId) : null
  })
  const [channel, setChannel] = useState<SpOnboardingChannel>(() =>
    surveyFirstPhone ? 'sms' : 'email',
  )
  const [otpCode, setOtpCode] = useState('')
  const [phone, setPhone] = useState(() =>
    surveyFirstPhone ? surveyFirstPhoneDisplay() : '',
  )
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [otpInlineError, setOtpInlineError] = useState<string | null>(null)
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null)
  const [resendCooldownUntilMs, setResendCooldownUntilMs] = useState<
    number | null
  >(null)
  const [otpExpiresAtMs, setOtpExpiresAtMs] = useState<number | null>(null)
  const [declinedKind, setDeclinedKind] = useState<ConsentDeclineKind | null>(
    null
  )
  const [showConsentDeclined, setShowConsentDeclined] = useState(false)
  const [postVerifyConsent, setPostVerifyConsent] = useState<{
    privacyRequired: boolean
    termsRequired: boolean
  } | null>(null)

  const lockoutRemaining = useLockoutRemaining(lockoutUntilMs)
  const resendRemaining = useLockoutRemaining(resendCooldownUntilMs)
  const isLockedOut = lockoutRemaining > 0
  const resendDisabled =
    resendRemaining > 0 ||
    otpResend.isPending ||
    isLockedOut ||
    otpVerify.isPending

  const goToInstrumentInfo = useCallback(
    (instrumentId: string) => {
      navigate(instrumentInfoPath(instrumentId), { replace: true })
    },
    [navigate]
  )

  const finishAfterConsent = useCallback(
    (instrumentId: string) => {
      runDetached(
        (async () => {
          const authed = await ensureSpSessionHydrated()
          if (!authed) {
            navigate('/login', {
              replace: true,
              state: instrumentLinkLoginState(instrumentId),
            })
            return
          }
          goToInstrumentInfo(instrumentId)
        })(),
      )
    },
    [goToInstrumentInfo, navigate],
  )

  const navigateToLogin = useCallback(
    (instrumentId: string) => {
      navigate('/login', {
        replace: true,
        state: instrumentLinkLoginState(instrumentId),
      })
    },
    [navigate]
  )

  const resolveConsentStep = useCallback(
    (
      requirements: { privacyRequired: boolean; termsRequired: boolean },
      instrumentId?: string
    ) => {
      resolveInstrumentConsentStep(
        requirements,
        instrumentId ?? session?.instrumentId,
        {
          finishAfterConsent,
          setStep,
          resetDeclined: () => setShowConsentDeclined(false),
        },
        { forceBothPolicies: signupShareFlow },
      )
    },
    [finishAfterConsent, session?.instrumentId, signupShareFlow],
  )

  const applyLinkStartResult = useCallback(
    (data: SpInstrumentLinkStartData) => {
      setSession(data)
      setPostVerifyConsent(data.consent)
      if (data.onboardingSessionId) {
        persistWebSurveyOnboardingSession(data.onboardingSessionId)
      }
      if (data.isVerified) {
        persistWebSurveyPhoneVerified()
      }
      if (
        shouldCaptureSurveyLinkClick(data.instrumentId) &&
        data.instrumentId
      ) {
        const linkChannel = parseSurveyLinkChannel(searchQuery)
        const spUserId = useSessionStore.getState().user?.id ?? null
        captureSpSurveyLinkClicked({
          link_channel: linkChannel,
          instrument_id: data.instrumentId,
          sp_user_id: spUserId,
        })
      }
      routeInstrumentLinkStartFlow(
        data,
        {
          goToInstrumentInfo,
          navigateToLogin,
          finishAfterConsent,
          setChannel,
          setStep,
        },
        signupShareFlow ? { forceBothPolicies: true } : undefined,
      )
      if (preferSms && (data.flow === 'onboarding' || data.flow == null)) {
        setChannel('sms')
      }
    },
    [
      finishAfterConsent,
      goToInstrumentInfo,
      navigateToLogin,
      preferSms,
      searchQuery,
      signupShareFlow,
    ],
  )

  useEffect(() => {
    if (surveyFirstPhone) {
      const setters = { setSession, setChannel, setStep, setServerError }
      if (applyResolvedSurveyFirstSession(storeUserId, setters)) return

      let cancelled = false
      runDetached(
        (async () => {
          await ensureSpSessionHydrated()
          if (cancelled) return
          if (
            applyResolvedSurveyFirstSession(
              useSessionStore.getState().user?.id,
              setters,
            )
          ) {
            return
          }
          setSession(null)
          setStep('channel')
          setServerError(INVALID_INSTRUMENT_LINK_MESSAGE)
        })(),
      )
      return () => {
        cancelled = true
      }
    }

    if (!linkToken) {
      setStep('loading')
      setServerError(INVALID_INSTRUMENT_LINK_MESSAGE)
      return
    }

    let cancelled = false
    setServerError(null)
    setStep('loading')

    startInstrumentLink(linkToken, {
      onSuccess: (data) => {
        if (cancelled || !data) return
        applyLinkStartResult(data)
      },
      onError: (error) => {
        if (cancelled) return
        setSession(null)
        setStep('channel')
        setServerError(resolveInstrumentLinkStartError(error))
      },
    })

    return () => {
      cancelled = true
    }
  }, [applyLinkStartResult, linkToken, startInstrumentLink, storeUserId, surveyFirstPhone])

  const applyOtpDeliverySuccess = useCallback(
    (data: SpInstrumentOtpSendData | undefined) => {
      setOtpCode('')
      setOtpInlineError(null)
      setStep('otp')
      setResendCooldownUntilMs(
        Date.now() + SP_INSTRUMENT_OTP_RESEND_COOLDOWN_SECONDS * 1000
      )
      if (data?.channel) {
        setChannel(data.channel)
      }
      if (data?.otpExpiresAt) {
        setOtpExpiresAtMs(new Date(data.otpExpiresAt).getTime())
      }
    },
    []
  )

  useEffect(() => {
    if (step !== 'otp' || otpExpiresAtMs === null) return

    const handleOtpExpired = () => {
      setServerError(INSTRUMENT_OTP_EXPIRED_MESSAGE)
      setOtpCode('')
      setOtpInlineError(null)
      setResendCooldownUntilMs(null)
    }

    const delay = otpExpiresAtMs - Date.now()
    if (delay <= 0) {
      handleOtpExpired()
      return
    }

    const timerId = globalThis.setTimeout(handleOtpExpired, delay)
    return () => globalThis.clearTimeout(timerId)
  }, [step, otpExpiresAtMs])

  const handleChannelSelect = useCallback(
    (nextChannel: SpOnboardingChannel) => {
      setChannel(nextChannel)
      setPhoneError(null)
      if (session?.instrumentId) {
        const spUserId = useSessionStore.getState().user?.id ?? null
        captureSpOtpChannelSelected({
          otp_channel: nextChannel,
          instrument_id: session.instrumentId,
          sp_user_id: spUserId,
        })
      }
    },
    [session?.instrumentId]
  )

  const handlePhoneChange = (raw: string) => {
    const next = channel === 'sms' ? formatUsMobileDisplay(raw) : raw
    setPhone(next)
    if (phoneError && (next.trim() === '' || isValidUsMobileNumber(next))) {
      setPhoneError(null)
    }
  }

  const handlePhoneBlur = () => {
    if (channel !== 'sms') return
    const next = formatUsMobileOnBlur(phone)
    setPhone(next)
    if (next && !isValidUsMobileNumber(next)) {
      setPhoneError(ONBOARDING_PHONE_ERROR)
    }
  }

  const handleSendOtp = () => {
    const onboardingSessionId = session?.onboardingSessionId
    if (!onboardingSessionId || !session?.channelAvailability) return
    if (!session.channelAvailability[channel]) return
    if (channel === 'sms') {
      if (!isValidUsMobileNumber(phone)) {
        setPhoneError(ONBOARDING_PHONE_ERROR)
        return
      }
    }
    setServerError(null)
    const e164 = channel === 'sms' ? normalizeUsMobileForStorage(phone) : ''

    const send = () => {
      otpSend.mutate(
        {
          onboardingSessionId,
          channel,
          ...(e164 ? { phone: e164 } : {}),
        },
        {
          onSuccess: (data) => {
            if (e164) persistWebSurveyPhone(e164)
            applyOtpDeliverySuccess(data)
          },
          onError: (error) => {
            const failure = resolveOtpSendError(error)
            if (error instanceof ApiError && error.status === 409 && channel === 'sms') {
              setPhoneError(error.message)
              return
            }
            if (failure.blocked && channel === 'sms') {
              setPhoneError(ONBOARDING_PHONE_DATASET_ERROR)
              return
            }
            if (failure.lockoutUntilMs) {
              setLockoutUntilMs(failure.lockoutUntilMs)
            }
            setServerError(failure.message)
          },
        },
      )
    }

    if (surveyFirstPhone && e164) {
      runDetached(
        (async () => {
          try {
            await phoneCheck.mutateAsync({
              onboardingSessionId,
              phone: e164,
            })
            send()
          } catch (error) {
            if (error instanceof ApiError && error.status === 409) {
              setPhoneError(error.message)
              return
            }
            setServerError(
              error instanceof Error ? error.message : 'Unable to verify this mobile number.',
            )
          }
        })(),
      )
      return
    }

    send()
  }

  const handleVerifyOtpSuccess = useCallback(
    async (data: SpInstrumentOtpVerifyData | undefined) => {
      if (!data) {
        setServerError('Verification failed. Please try again.')
        return
      }
      setSession((prev) =>
        prev
          ? {
              ...prev,
              instrumentId: data.instrumentId,
              campaignId: data.campaignId,
            }
          : prev
      )
      setPostVerifyConsent({
        privacyRequired: data.consent.privacyRequired,
        termsRequired: data.consent.termsRequired,
      })
      await ensureSpSessionHydrated()
      persistWebSurveyPhoneVerified()
      if (surveyFirstPhone) {
        navigate('/instrument/onboarding/account', { replace: true })
        return
      }
      resolveConsentStep(data.consent, data.instrumentId)
    },
    [resolveConsentStep, surveyFirstPhone, navigate]
  )

  const handleVerifyOtp = () => {
    if (!session?.onboardingSessionId || otpCode.length !== 6 || isLockedOut)
      return
    setServerError(null)
    setOtpInlineError(null)
    otpVerify.mutate(
      { onboardingSessionId: session.onboardingSessionId, code: otpCode },
      {
        onSuccess: (data) => {
          runDetached(handleVerifyOtpSuccess(data))
        },
        onError: (error) => {
          const failure = resolveOtpVerifyError(error)
          if (failure.lockoutUntilMs) {
            setLockoutUntilMs(failure.lockoutUntilMs)
          }
          if (failure.serverMessage) {
            setServerError(failure.serverMessage)
            return
          }
          if (failure.inlineMessage) {
            setOtpInlineError(failure.inlineMessage)
          }
        },
      }
    )
  }

  const handleResendOtp = () => {
    if (!session?.onboardingSessionId || resendDisabled) return
    setServerError(null)
    setOtpInlineError(null)
    otpResend.mutate(session.onboardingSessionId, {
      onSuccess: (data) => {
        applyOtpDeliverySuccess(data)
      },
      onError: (error) => {
        const failure = resolveOtpResendError(error)
        if (failure.resendCooldownUntilMs) {
          setResendCooldownUntilMs(failure.resendCooldownUntilMs)
        }
        if (failure.message) {
          setServerError(failure.message)
        }
      },
    })
  }

  const handleAcceptPrivacy = async () => {
    if (!session) return
    if (
      !(await ensureInstrumentLinkSessionOrLogin(
        session.instrumentId,
        navigate
      ))
    )
      return
    setServerError(null)
    acceptPrivacy.mutate(SP_PRIVACY_POLICY_VERSION, {
      onSuccess: (data) => {
        posthog.capture('instrument_link_consent_accepted', {
          consent_type: 'privacy',
        })
        const termsRequired =
          data?.termsRequired ?? postVerifyConsent?.termsRequired ?? false
        if (signupShareFlow) {
          setShowConsentDeclined(false)
          setStep('terms')
          return
        }
        resolveInstrumentConsentStep(
          { privacyRequired: false, termsRequired },
          session.instrumentId,
          {
            finishAfterConsent,
            setStep,
            resetDeclined: () => {
              setShowConsentDeclined(false)
            },
          }
        )
      },
      onError: (error) => {
        setServerError(resolveConsentMutationError(error))
      },
    })
  }

  const handleAcceptTerms = async () => {
    if (!session) return
    if (
      !(await ensureInstrumentLinkSessionOrLogin(
        session.instrumentId,
        navigate
      ))
    )
      return
    setServerError(null)

    const afterTermsAccepted = () => {
      posthog.capture('instrument_link_consent_accepted', {
        consent_type: 'terms',
      })
      finishAfterConsent(session.instrumentId)
    }

    acceptTerms.mutate(SP_TERMS_POLICY_VERSION, {
      onSuccess: afterTermsAccepted,
      onError: (error) => {
        setServerError(resolveConsentMutationError(error))
      },
    })
  }

  const handleDeclineConsent = (kind: ConsentDeclineKind) => {
    setDeclinedKind(kind)
    setShowConsentDeclined(true)
  }

  const handleRetryConsent = () => {
    setShowConsentDeclined(false)
    setStep(resolveRetryConsentStep(declinedKind))
  }

  const maskedContact =
    channel === 'sms'
      ? (session?.maskedPhone ?? '')
      : (session?.maskedEmail ?? '')

  const showOtpOnboarding =
    session?.flow === 'onboarding' && Boolean(session.onboardingSessionId)

  return {
    linkToken,
    linkStartPending,
    step,
    session,
    channel,
    setChannel: handleChannelSelect,
    otpCode,
    setOtpCode,
    phone,
    setPhone,
    handlePhoneChange,
    handlePhoneBlur,
    phoneError,
    isUsPhoneValid: isValidUsMobileNumber(phone),
    serverError,
    otpInlineError,
    lockoutUntilMs,
    isLockedOut,
    resendDisabled,
    resendRemaining,
    showConsentDeclined,
    maskedContact,
    showOtpOnboarding,
    otpSend,
    phoneCheck,
    otpVerify,
    acceptPrivacy,
    acceptTerms,
    handleSendOtp,
    handleVerifyOtp,
    handleResendOtp,
    handleAcceptPrivacy,
    handleAcceptTerms,
    handleDeclineConsent,
    handleRetryConsent,
    surveyFirstPhone,
  }
}

export type InstrumentLinkOnboardingViewModel = ReturnType<
  typeof useInstrumentLinkOnboarding
>
